import { Badge, Button } from '../../../shared/ui'
import type { OutreachOverview, OverviewBusinessStats } from '../../../lib/outreach/types'
import { formatCount } from '../../../lib/outreach/stats'
import { setActiveWorkspace } from '../../../lib/workspace'
import { KpiTiles } from './KpiTiles'
import { Section } from './Section'
import { SimpleTable, type Column } from './SimpleTable'
import { countWithRate } from './format'

/** Active programs that are not sending right now. */
export function pausedProgramCount(b: OverviewBusinessStats): number {
  return b.stats.by_program.filter((p) => p.active && p.paused_reason !== null).length
}

function columns(currentId: string | null): Column<OverviewBusinessStats>[] {
  return [
    {
      key: 'name',
      label: 'Business',
      render: (b) => (
        <span className="od-name-cell">
          {b.name}
          {b.workspace_id === currentId && <Badge tone="ok">Current</Badge>}
        </span>
      ),
    },
    { key: 'role', label: 'Role', render: (b) => b.role },
    {
      key: 'today',
      label: 'Sent today',
      numeric: true,
      render: (b) => formatCount(b.stats.totals.sent_today),
    },
    {
      key: 'd7',
      label: 'Sent 7d',
      numeric: true,
      render: (b) => formatCount(b.stats.totals.sent_7d),
    },
    {
      key: 'd30',
      label: 'Sent 30d',
      numeric: true,
      render: (b) => formatCount(b.stats.totals.sent_30d),
    },
    {
      key: 'replies',
      label: 'Replies',
      numeric: true,
      render: (b) => countWithRate(b.stats.totals.replies, b.stats.totals.reply_rate),
    },
    {
      key: 'bounces',
      label: 'Bounces',
      numeric: true,
      render: (b) => countWithRate(b.stats.totals.bounces, b.stats.totals.bounce_rate),
    },
    {
      key: 'queued',
      label: 'Queued',
      numeric: true,
      render: (b) => formatCount(b.stats.totals.queued),
    },
    {
      key: 'due',
      label: 'Due today',
      numeric: true,
      render: (b) => formatCount(b.stats.totals.due_today),
    },
    {
      key: 'paused',
      label: 'Paused programs',
      numeric: true,
      render: (b) => formatCount(pausedProgramCount(b)),
    },
    {
      key: 'open',
      label: 'Open',
      render: (b) => (
        <Button
          variant="secondary"
          disabled={b.workspace_id === currentId}
          aria-label={`Open ${b.name}`}
          onClick={() => setActiveWorkspace(b.workspace_id)}
        >
          Open
        </Button>
      ),
    },
  ]
}

interface Props {
  overview: OutreachOverview
  currentId: string | null
  days: number
}

export function OverviewSection({ overview, currentId, days }: Props) {
  return (
    <>
      <KpiTiles totals={overview.totals} days={days} />
      <Section
        title="By business"
        hint={`Sent today, 7d and 30d are fixed windows. Replies and bounces cover the last ${days} days.`}
      >
        <SimpleTable
          label="By business"
          columns={columns(currentId)}
          rows={overview.businesses}
          rowKey={(b) => b.workspace_id}
          rowClass={(b) => (b.workspace_id === currentId ? 'od-current' : undefined)}
          emptyText="No businesses."
        />
      </Section>
    </>
  )
}
